<script setup>
import { reactive, onMounted, computed, watch, ref, nextTick } from 'vue';
import Blokliste from '@/Components/Blokke/Blokliste.vue';
import { usePage } from '@inertiajs/vue3';
import LeftMenuWrapper from '@/Components/LeftMenu/LeftMenuWrapper.vue';
import ProductLinkList from '@/Components/ProductLinkList.vue';
import axios from 'axios';
import { updateQueryParamsAndReload } from '@/Components/FilterManager.vue';
import TableViewTable from '@/Components/Product/TableViewTable.vue';
import ProductListRow from '@/Components/Product/ProductListRow.vue';
import ProductCompactRow from '@/Components/Product/ProductCompactRow.vue';
import ProductGridCard from '@/Components/Product/ProductGridCard.vue';
import ViewModeToggle from '@/Components/Product/ViewModeToggle.vue';
import SortingWidget from '@/Components/Category/SortingWidget.vue';
import ProductSkeleton from '@/Components/Product/ProductSkeleton.vue';
import MobileFilterDrawer from '@/Components/MobileFilterDrawer.vue';
import BrandStrip from '@/Components/Category/BrandStrip.vue';
import Byggeforslag from '@/Components/PcBygger/Byggeforslag.vue';
import RelatedCategories from '@/Components/Category/RelatedCategories.vue';
import BreadcrumbBar from '@/Components/BreadcrumbBar.vue';
import PriceCompareLayout from '../../Layouts/PriceCompareLayout.vue';
import { marked } from 'marked';
import { sanitizeHtml } from '@/Utils/sanitize';
import NyhedsBlok from '@/Components/NyhedsBlok.vue';

const viewMode = ref('list');
const showMobileFilters = ref(false);

const page = usePage();

const nyheder = computed(() => page.props.nyheder || []);

// Server-renderede produktlinks. Se kommentaren ved ProductLinkList nedenfor.
const productLinks = computed(() => page.props.productLinks || []);

// Landingssidens egen overskrift. Se kommentaren ved h1 nedenfor.
const landingsideH1 = computed(() => page.props.landingPage?.h1_title || null);

/*
 * Faerdige maskiner, kun paa de tolv kategorier byggeren henter dele fra.
 * null overalt ellers, saa sektionen koster ingenting paa de oevrige 179.
 */
const byggeforslag = computed(() => page.props.byggeforslag || null);

// Prisoversigten. Se kommentaren ved sektionen nedenfor.
const prisoversigt = computed(() => page.props.prisoversigt || null);
const prisudvikling = computed(() => page.props.prisudvikling || null);

// Indholdsblokke. Kun landingssider har dem; tom paa almindelige
// kategorisider, saa de koster ingenting dér.
const blokke = computed(() => page.props.blokke || []);
const faq = computed(() => page.props.faq || []);
const prisoversigtOverskrift = computed(() => page.props.prisoversigtOverskrift || '');

const user = computed(() => page.props.auth.user);
const breadcrumbs = computed(() => page.props.breadcrumbs);
const pageTitle = computed(() => page.props.pageTitle);
const search = computed(() => page.props.search);
const category = computed(() => page.props.category);
const currentCategory = computed(() => page.props.subSubCategory);
const listSpecColumns = computed(() => page.props.listSpecColumns || []);
// Kategoriens enhed, fx "TB". Null når der ikke er en mængde at dividere med,
// og så vises hverken enhedspris eller sorteringen efter den.
const unitPriceLabel = computed(() => page.props.unitPriceLabel || null);

const topSeoText = computed(() => {
  const filterText = state.filterTopSeoText;
  const categoryText = currentCategory.value?.top_seo_intro;
  const source = filterText || categoryText;
  return source ? sanitizeHtml(marked.parse(source)) : '';
});

const bottomSeoText = computed(() => {
  const filterText = state.filterBottomSeoText;
  const categoryText = currentCategory.value?.bottom_seo_intro;
  const source = filterText || categoryText;
  return source ? sanitizeHtml(marked.parse(source)) : '';
});

// Add reactive seoData object initialized from server props
const initialSeoData = computed(() => page.props.seo || {});
const seoData = reactive({
    title: initialSeoData.value?.title || '',
    description: initialSeoData.value?.description || '',
    keywords: initialSeoData.value?.keywords || '',
    robots: 'index, follow', // Default robots value
    openGraph: initialSeoData.value?.openGraph || {},
    structuredData: initialSeoData.value?.structuredData || []
});

// Watch for seoData changes to update document head
watch(seoData, (newSeoData) => {
    updateDocumentHead(newSeoData);
}, { deep: true });

// Function to update document head elements
function updateDocumentHead(seoData) {
    if (typeof document === 'undefined') return;
    document.title = seoData.title;
    
    // Update or create meta tags
    updateMetaTag('description', seoData.description);
    updateMetaTag('keywords', seoData.keywords);
    updateMetaTag('robots', seoData.robots);
    
    // Update OpenGraph meta tags
    if (seoData.openGraph) {
        updateMetaTag('og:title', seoData.openGraph.title);
        updateMetaTag('og:description', seoData.openGraph.description);
        updateMetaTag('og:image', seoData.openGraph.image);
        updateMetaTag('og:type', seoData.openGraph.type);
    }
}

// Helper function to update or create meta tag
function updateMetaTag(name, content) {
    if (!content || typeof document === 'undefined') return;

    let metaTag = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
    
    if (metaTag) {
        metaTag.setAttribute('content', content);
    } else {
        metaTag = document.createElement('meta');
        // Determine if we should use name or property attribute (OG tags use property)
        if (name.startsWith('og:')) {
            metaTag.setAttribute('property', name);
        } else {
            metaTag.setAttribute('name', name);
        }
        metaTag.setAttribute('content', content);
        document.head.appendChild(metaTag);
    }
}

const usedFilters = reactive({
    filters: [],
});

// Replace queryParameters initialization with $page.props.query
const queryParameters = ref(new URLSearchParams(page.props.query));

// Update watch to use $page.props.query instead of window.location.search
watch(() => page.props.query, (newQuery) => {
    queryParameters.value = new URLSearchParams(newQuery);
}, { immediate: true });

const initialFilterSeo = page.props.initialFilterSeo || null;

// Læs side og sortering ud af URL'en, så et delt eller bogmærket link viser
// det man delte. Siden hentede før altid side 1 med standardsorteringen og
// fjernede derefter ?page= igen, så side 2 hverken kunne deles eller
// genindlæses, og en crawler der fulgte ?page=2 fik side 1.
function sideFraUrl() {
    const p = parseInt(queryParameters.value.get('page') || '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
}

function sorteringFraUrl() {
    const s = queryParameters.value.get('sort');
    return s ? s.toUpperCase() : 'POPULARITY';
}

const state = reactive({
    products: [],
    loading: false,
    page: sideFraUrl(),
    perPage: 50,
    currentSortKey: sorteringFraUrl(),
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
    filterH1: initialFilterSeo?.h1 || null,
    filterTopSeoText: initialFilterSeo?.topSeoText || null,
    filterBottomSeoText: initialFilterSeo?.bottomSeoText || null,
});

onMounted(() => {
    // Varerne hentes af categoryId-vagten, som koerer med immediate: true og
    // altsaa allerede har fyret naar vi naar hertil. Hentede vi ogsaa her,
    // ville hver sidevisning koste to ens opslag.
    updateDocumentHead(seoData);
});

function onFiltersLoaded() {
    // Filters just loaded — wait for Vue to render the DOM, then fetch counts
    nextTick(() => {
        updateProductCountLabels();
    });
}

async function onFilterUpdated(filters) {
    usedFilters.filters = filters;

    // Build new query object for URL params
    const newQuery = {};
    for (const filter of filters) {
        // For minmaxselect, value is type|value (e.g. min|12000000)
        // For checkboxes, value is a comma-separated string
        newQuery[filter.id] = filter.value;
    }

    // Update URL with new filters using Inertia visit
    if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        // Remove all previous filter params (except sort/page)
        for (const key of Array.from(url.searchParams.keys())) {
            if (key !== 'sort' && key !== 'page') {
                url.searchParams.delete(key);
            }
        }
        // Add new filter params
        for (const [key, value] of Object.entries(newQuery)) {
            if (value && value !== '') {
                url.searchParams.set(key, value);
            }
        }
        const qs = url.searchParams.toString();
        window.history.replaceState({}, '', qs ? `${url.pathname}?${qs}` : url.pathname);
        queryParameters.value = new URLSearchParams(url.searchParams.toString());
    }

    await getProducts(1);
    updateProductCountLabels();
}

// Abort controllers for chunked batch requests
let batchCountControllers = [];
const BATCH_CHUNK_SIZE = 15;

/** Hvor mange hold der maa vaere i luften samtidig. Se updateProductCountLabels. */
const SAMTIDIGE_HOLD = 4;

function updateProductCountLabels() {
    // SSR safety: only run in browser
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Cancel all previous in-flight batch requests
    batchCountControllers.forEach(c => c.abort());
    batchCountControllers = [];

    /*
     * KUN DE SYNLIGE.
     *
     * Sammenfoldede filtre bliver staaende i DOM'en (v-show), saa
     * querySelectorAll finder ogsaa deres vaerdier. Da filterloftet blev
     * hoevet fra ti til alle, blev det til tusindvis: Switch har 304 filtre
     * med 16.823 vaerdier, og med hold paa femten blev det 1.121 samtidige
     * POST'er fra én sidevisning.
     *
     * Maalt natten til 16. september 2026: 8.590 "no live upstreams" i
     * nginx — PHP-FPM loeb toer for processer, og sitet svarede 502 i
     * boelger fra 00:42 til 06:10. 8.215 af dem var count-batch.
     *
     * offsetParent er null for alt med display:none. Det koster ingenting
     * at spoerge om, og et tal ingen kan se, er ikke vaerd at hente.
     */
    const labels = [...document.querySelectorAll('.js-filter-count-label')]
        .filter((el) => el.offsetParent !== null);
    if (labels.length === 0) return;

    // Build active filters string from URL params
    const activeFilters = {};
    for (const [key, value] of queryParameters.value.entries()) {
        if (key !== 'sort' && key !== 'page') {
            activeFilters[key] = decodeURIComponent(value);
        }
    }
    const activeFilterString = Object.entries(activeFilters)
        .map(([k, v]) => `${k}:${v}`)
        .join('|');

    // Collect unique filter+value pairs grouped by filterId (one chunk per filter)
    const filterValues = [];
    const labelMap = {}; // key -> [label elements]
    labels.forEach(label => {
        const filterId = label.getAttribute('data-filter-id');
        const filterValue = label.getAttribute('data-filter-value');
        if (!filterId || !filterValue) return;
        const key = `${filterId}:${filterValue}`;
        if (!labelMap[key]) {
            labelMap[key] = [];
            filterValues.push({ filterId, value: filterValue });
        }
        labelMap[key].push(label);
    });

    /*
     * Hold paa hold, ikke alle paa én gang.
     *
     * Selv med kun de synlige kan en stor kategori give mange hold, og
     * browseren fyrer dem af samtidig. Fire ad gangen er nok til at det
     * foeles oejeblikkeligt og faa nok til at serveren kan foelge med.
     */
    const hold = [];
    for (let i = 0; i < filterValues.length; i += BATCH_CHUNK_SIZE) {
        hold.push(filterValues.slice(i, i + BATCH_CHUNK_SIZE));
    }

    const hentHold = async (chunk) => {
        const controller = new AbortController();
        batchCountControllers.push(controller);

        try {
            const response = await axios.post(`/api/v1/products/${categoryId.value}/count-batch`, {
                activeFilters: activeFilterString,
                filterValues: chunk,
            }, { signal: controller.signal });

            const counts = response.data?.counts || {};
            for (const [key, count] of Object.entries(counts)) {
                if (labelMap[key]) {
                    labelMap[key].forEach(label => {
                        label.textContent = count;
                    });
                }
            }
        } catch (error) {
            if (axios.isCancel?.(error) || error?.name === 'CanceledError') return;
            console.error('Error fetching batch product counts:', error);
        }
    };

    let naeste = 0;
    const arbejder = async () => {
        while (naeste < hold.length) {
            await hentHold(hold[naeste++]);
        }
    };

    for (let i = 0; i < Math.min(SAMTIDIGE_HOLD, hold.length); i++) {
        arbejder();
    }
}

const emit = defineEmits(['sortingUpdated']);

async function onSortingUpdated(sorting) {
    state.currentSortKey = sorting;

    // Skriv valget i URL'en, så en sorteret liste kan deles og genindlæses.
    updateSortingInUrl(sorting);

    await getProducts(1);
}

const categoryId = computed(() => page.props.subSubCategory && page.props.subSubCategory.id ? page.props.subSubCategory.id : null);

const currentActiveFilters = reactive({});

// Update getProducts function to use queryParameters safely
async function getProducts(showpage = 1) {
    try {
        state.loading = true;

        // Extract active filters from queryParameters
        const activeFilters = {};
        for (const [key, value] of queryParameters.value.entries()) {
            if (key !== 'sort' && key !== 'page') {
                const decodedValue = decodeURIComponent(value);
                activeFilters[key] = decodedValue;
            }
        }

        //add active filters to currentActiveFilters
        Object.assign(currentActiveFilters, activeFilters);

        const filterString = Object.entries(activeFilters)
            .map(([key, value]) => `${key}:${value}`)
            .join('|');

        const sortFromUrl = queryParameters.value.get('sort');
        const sort = sortFromUrl ? sortFromUrl.toUpperCase() : state.currentSortKey;

        state.currentSortKey = sort;

        // Update page number in URL
        updatePageInUrl(showpage);

        const response = await axios.get(`/api/v1/products/${categoryId.value}/`, {
            params: {
                page: showpage,
                per_page: state.perPage,
                filters: filterString,
                sort: sort
            }
        });

        const data = response.data;

        state.products = data.products;
        state.page = data.pagination.current_page;
        state.totalPages = data.pagination.total_pages;
        state.totalItems = data.pagination.total_items;
        state.hasNextPage = data.pagination.has_next_page;
        state.hasPrevPage = data.pagination.has_prev_page;
        state.currentSortKey = sort;

        // Update SEO data based on API response if available
        if (data.seoData && Object.keys(data.seoData).length > 0) {
            seoData.title = data.seoData.title || seoData.title;
            seoData.description = data.seoData.description || seoData.description;
            seoData.keywords = data.seoData.keywords || seoData.keywords;
            seoData.robots = data.seoData.robots || 'index, follow';

            if (data.seoData.h1) {
                state.filterH1 = data.seoData.h1;
            }
            if (data.seoData.topSeoText) {
                state.filterTopSeoText = data.seoData.topSeoText;
            }
            if (data.seoData.bottomSeoText) {
                state.filterBottomSeoText = data.seoData.bottomSeoText;
            }

            if (data.seoData.openGraph) {
                seoData.openGraph = {...seoData.openGraph, ...data.seoData.openGraph};
            }
            if (data.seoData.structuredData) {
                seoData.structuredData = data.seoData.structuredData;
            }
        } else {
            state.filterH1 = null;
            state.filterTopSeoText = null;
            state.filterBottomSeoText = null;
        }

        state.loading = false;
    } catch (error) {
        console.error('Error fetching products:', error);
        state.loading = false;
    }
}

// Watch categoryId — placed after getProducts is defined to avoid reference errors
// Skip during SSR (products are fetched client-side via API)
watch(categoryId, (newVal, oldVal) => {
    if (typeof window === 'undefined') return;
    if (! newVal || newVal === oldVal) return;

    /*
     * FOERSTE koersel skal bruge siden fra adressen.
     *
     * Vagten her koerer med immediate: true, altsaa FOER onMounted, og kaldte
     * getProducts(1) uden at se paa URL'en. Den vandt over onMounteds
     * getProducts(sideFraUrl()), og resultatet var at et direkte kald af
     * ?page=2 viste side 1: samme foerste vare, "1" markeret i pagineringen,
     * forrige-knappen deaktiveret.
     *
     * Det ramte baade delte links og Google, som crawler ?page=2 til ?page=21
     * og fik den samme side hver gang.
     *
     * Skifter kategorien BAGEFTER, er side 1 stadig det rigtige.
     */
    const side = oldVal === undefined ? sideFraUrl() : 1;

    state.products = [];
    state.page = side;
    getProducts(side);
}, { immediate: true });

// Ensure sort parameter is always last in URL and lowercase
function updateSortingInUrl(sortKey) {
    const searchParams = new URLSearchParams(window.location.search);
    
    // Store current sort value (ensure uppercase internally)
    const currentSort = sortKey || searchParams.get('sort') || state.currentSortKey;
    
    // Remove sort from current params (to add it back at the end)
    searchParams.delete('sort');
    
    // Add sort as the last parameter (convert to lowercase for URL)
    searchParams.append('sort', currentSort.toLowerCase());

    // Update URL without reloading the page (udelad nøgent "?" hvis tom)
    const qs = searchParams.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    // Kun push hvis URL'en faktisk ændrer sig — ellers tilføjes en dublet
    // history-entry der bryder browserens tilbage-knap.
    if (newUrl !== window.location.pathname + window.location.search) {
        window.history.pushState({}, '', newUrl);
    }

    // Also update our reactive queryParameters
    queryParameters.value = new URLSearchParams(window.location.search);
}

// Function to update page parameter in URL
function updatePageInUrl(page) {
    const searchParams = new URLSearchParams(window.location.search);
    
    if (page === 1) {
        // Remove page parameter for first page to keep URL clean
        searchParams.delete('page');
    } else {
        // Set page parameter for other pages
        searchParams.set('page', page.toString());
    }

    // Update URL without reloading the page (udelad nøgent "?" hvis tom)
    const qs = searchParams.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    // Kun push hvis URL'en faktisk ændrer sig — ellers tilføjes en dublet
    // history-entry der bryder browserens tilbage-knap.
    if (newUrl !== window.location.pathname + window.location.search) {
        window.history.pushState({}, '', newUrl);
    }

    // Also update our reactive queryParameters
    queryParameters.value = new URLSearchParams(window.location.search);
}

function getQueryString(queryParameters) {
    const filters = {};

    for (const [key, value] of queryParameters.entries()) {
        if (value.trim() !== '') { // Remove empty values
            filters[key] = filters[key] ? `${filters[key]},${value}` : value;
        }
    }

    return Object.entries(filters)
        .map(([key, value]) => `${key}:${value}`)
        .join('|');
}

async function loadPrevPage() {
    if (state.page > 1) {
        await getProducts(state.page - 1);
    }
}

async function loadNextPage() {
    if (state.hasNextPage) {
        await getProducts(state.page + 1);
    }
}

async function loadSpecificPage(page) {
    if (page >= 1 && page <= state.totalPages && page !== state.page) {
        await getProducts(page);
    }
}

async function loadMore() {
    state.page++;
    await getProducts();
}


</script>

<template>
    <PriceCompareLayout>
        <div class="container-fluid text-center g-0">
            <div class="breadcrumb-spacing">
                <BreadcrumbBar :breadcrumbs="breadcrumbs" />
            </div>
            <div class="row g-0">
                <!-- Left menu column -->
                <div class="col-sm-12 col-md-3 col-lg-2 float-modal-mobile ps-md-3">
                    <LeftMenuWrapper @filterUpdated="onFilterUpdated" @filtersLoaded="onFiltersLoaded" @foldningAendret="updateProductCountLabels" :categoryId="categoryId || ''" :queryParameters="queryParameters" />
                </div>
                <!-- Main content column -->
                <div class="col main-content-block">
                    <!-- Category Title and SEO Text Card -->
                    <div class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <!--
                                h1 på en landingsside skal nævne mærket.

                                Siden satte pageTitle = h1_title ("Philips Pc skærm"),
                                men komponenten renderede kategorinavnet. Resultatet var
                                435 landingssider med præcis samme h1 som forældre-
                                kategorien: Google så 435 sider der alle hed "Pc skærm".
                            -->
                            <h1 class="h3 mb-2" style="color: var(--pc-text-primary);">{{ state.filterH1 || landingsideH1 || (page.props.subSubCategory.name + ' priser') }}</h1>
                            <div v-if="topSeoText && topSeoText.trim() !== ''" class="small" style="color: var(--pc-text-secondary);" v-html="topSeoText"></div>
                            <div v-else-if="seoData.description" class="small" style="color: var(--pc-text-secondary);">
                                {{ seoData.description }}
                            </div>
                        </div>
                    </div>
                    <BrandStrip v-if="page.props.categoryBrands && page.props.categoryBrands.length > 0"
                                :brands="page.props.categoryBrands"
                                :landingPages="page.props.categoryLandingPages || []"
                                :categoryPath="page.props.categoryPath || ''" />
                    <div class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body p-0">
                            <div>
                                <!-- Værktøjslinje: visningsform, antal og sortering.
                                     Sorteringen lå før nede i TableViewTable under
                                     hele listen, altså cirka fem skærmhøjder nede,
                                     og kunne i praksis ikke findes. -->
                                <div v-if="!state.loading && Array.isArray(state.products) && state.products.length > 0" class="liste-vaerktoejslinje">
                                    <div class="liste-vaerktoejslinje__venstre">
                                        <ViewModeToggle v-model="viewMode" />
                                        <span class="liste-vaerktoejslinje__antal">{{ state.totalItems }} produkter</span>
                                    </div>
                                    <SortingWidget
                                        :selectedSort="state.currentSortKey"
                                        :unitPriceLabel="unitPriceLabel"
                                        @sortingUpdated="onSortingUpdated"
                                    />
                                </div>

                                <!-- Mobile filter button (shown only on small screens) -->
                                <button
                                    class="btn-pc-secondary mobile-filter-btn"
                                    @click="showMobileFilters = true"
                                    style="display: none;"
                                >
                                    <i class="bi bi-funnel"></i> Filtre
                                </button>

                                <!-- Loading -->
                                <ProductSkeleton v-if="state.loading" :count="10" />

                                <!-- Product list view (default) -->
                                <div v-if="!state.loading && viewMode === 'list' && Array.isArray(state.products) && state.products.length > 0">
                                    <ProductListRow v-for="product in state.products" :key="product.id" :product="product" :activeFilters="currentActiveFilters" :specColumns="listSpecColumns" :unitPriceLabel="unitPriceLabel" />
                                </div>

                                <!-- Product compact view -->
                                <div v-if="!state.loading && viewMode === 'compact' && Array.isArray(state.products) && state.products.length > 0">
                                    <ProductCompactRow v-for="product in state.products" :key="product.id" :product="product" :activeFilters="currentActiveFilters" />
                                </div>

                                <!-- Product grid view -->
                                <div v-if="!state.loading && viewMode === 'grid' && Array.isArray(state.products) && state.products.length > 0" class="product-grid">
                                    <ProductGridCard v-for="product in state.products" :key="product.id" :product="product" />
                                </div>

                                <!-- Sidenavigation under listen -->
                                <TableViewTable
                                    v-if="!state.loading && Array.isArray(state.products) && state.products.length > 0"
                                    :loadNextPage="loadNextPage"
                                    :loadPrevPage="loadPrevPage"
                                    :loadSpecificPage="loadSpecificPage"
                                    :currentPage="state.page"
                                    :totalPages="state.totalPages"
                                />

                                <!-- No products -->
                                <div v-if="!state.loading && (!Array.isArray(state.products) || state.products.length === 0)" style="text-align: center; padding: 48px; color: var(--pc-text-muted);">
                                    <p>Ingen produkter fundet.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!--
                        Hvad koster varerne her?

                        279 prissøgeord ("billig server", "tv tilbud",
                        "overvågningskamera pris") giver 1.944 eksponeringer på
                        gennemsnitsposition 35,4 og TO klik. For hver af dem
                        findes der en kategoriside, men den hedder "Tv" og har
                        179 tegn brødtekst. Der fandtes ingen side der svarede
                        på spørgsmålet.

                        Tallene er vores egne målinger og opdateres af sig selv.
                    -->
                    <section v-if="prisoversigt" class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <h2 class="h5 mb-3">{{ prisoversigtOverskrift }}</h2>
                            <p class="mb-0" style="color: var(--pc-text-secondary);">{{ prisoversigt.tekst }}</p>
                            <!--
                              Bevaegelsen, ikke bare niveauet. "Er priserne paa
                              vej ned" er det spoergsmaal en koeber faktisk
                              stiller, og svaret staar i HTML saa baade Google og
                              en sprogmodel kan laese det.
                            -->
                            <p v-if="prisudvikling" class="mb-0 mt-3" style="color: var(--pc-text-secondary);">{{ prisudvikling.tekst }}</p>
                        </div>
                    </section>

                    <!--
                        Produktlinks i rå HTML.

                        Produktlisten ovenfor hentes via API efter hydrering, så
                        denne side leverede NUL produktlinks i det HTML en crawler
                        eller en sprogmodel får. pc-skaerm rummer 3.132 varer og
                        pegede på ingen af dem. Det er en stor del af forklaringen
                        på at produktsiderne står som isolerede sider uden intern
                        autoritet: de kunne kun findes gennem sitemappet, og det
                        svarede 404 efter hvert deploy.
                    -->

                    <!--
                      Spoergsmaal og svar, samme mekanik som produktsiderne hvor
                      den virker. Svarene staar i HTML og ikke kun i JSON-LD: et
                      svar der kun findes i skema-opmaerkning kan Google afvise
                      som skjult indhold, og et menneske faar intet ud af det.
                      Tom liste naar der ikke er nok at sige; se KategoriFaq.
                    -->
                    <section v-if="faq.length" class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <h2 class="h5 mb-3">Ofte stillede spørgsmål</h2>
                            <div v-for="(q, i) in faq" :key="i" :class="i > 0 ? 'mt-3' : ''">
                                <h3 class="h6 mb-1">{{ q.spoergsmaal }}</h3>
                                <p class="mb-0" style="color: var(--pc-text-secondary);">{{ q.svar }}</p>
                            </div>
                        </div>
                    </section>

                    <!--
                        Byggeforslag, foer produktlinkene.

                        De tolv komponentkategorier fik 1.609 eksponeringer og
                        9 klik paa seks uger uden at naevne at vi kan saette
                        delene sammen. Kortene staar i server-renderet HTML og
                        linker baade til build-siderne og til hver enkelt del.
                    -->
                    <Byggeforslag v-if="byggeforslag" :forslag="byggeforslag" />

                    <ProductLinkList :products="productLinks" title="Populære produkter i kategorien" />

                    <RelatedCategories
                        v-if="page.props.relatedCategories && page.props.relatedCategories.length > 0"
                        :categories="page.props.relatedCategories"
                    />
                    <!-- Long SEO Text Card -->
                    <!-- Indholdsblokke, over bundteksten. Kun paa landingssider. -->
                    <div v-if="blokke.length" class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body">
                            <Blokliste :blokke="blokke" />
                        </div>
                    </div>

                    <div v-if="bottomSeoText && bottomSeoText.trim() !== ''" class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <div class="seo-long-text" style="color: var(--pc-text-secondary);" v-html="bottomSeoText"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <MobileFilterDrawer :show="showMobileFilters" @close="showMobileFilters = false">
            <LeftMenuWrapper
                @filterUpdated="onFilterUpdated"
                @filtersLoaded="onFiltersLoaded"
                @foldningAendret="updateProductCountLabels"
                :categoryId="categoryId"
                :queryParameters="queryParameters"
            />
        </MobileFilterDrawer>
        <div class="container">
            <NyhedsBlok :nyheder="nyheder" overskrift="Nyheder i denne kategori" />
        </div>
    </PriceCompareLayout>
</template>

<style scoped>
.liste-vaerktoejslinje {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
    border-bottom: 1px solid var(--pc-border);
    margin-bottom: 8px;
}

.liste-vaerktoejslinje__venstre {
    display: flex;
    align-items: center;
    gap: 12px;
}

.liste-vaerktoejslinje__antal {
    font-size: var(--pc-text-sm);
    color: var(--pc-text-muted);
    white-space: nowrap;
}

@media (max-width: 575.98px) {
    .liste-vaerktoejslinje {
        flex-wrap: wrap;
        row-gap: 8px;
    }
}

.main-content-block {
    padding-left: 15px;
    padding-right: 15px;

    /*
     * RØR IKKE min-width. Uden den brækker hele siden.
     *
     * En Bootstrap-col har min-width: auto, og en flex-boks kan ikke blive
     * smallere end sit indholds min-content. Produktlisten har rækker hvis
     * min-content er over 1.200 px (et navn med en ubrudt streng som
     * "Dronewww.dji.com/" er nok), og så kan kolonnen ikke være ved siden af
     * filtrene. Den brød derfor ned på en ny flex-linje UNDER dem.
     *
     * Målt på /kategorier/foto-og-video/video/droner ved 1440 px: første vare
     * lå 14.243 px nede, efter en filterkolonne på 196 aktive filtre. Med
     * min-width: 0 ligger den 151 px nede, ved siden af filtrene, og kolonnen
     * er 1.200 px i stedet for 1.440.
     *
     * Ordbrydning på navnet retter det IKKE. Jeg prøvede begge dele i
     * browseren mod produktionen: overflow-wrap alene flyttede ingenting.
     */
    min-width: 0;
}

.card {
    border-radius: var(--pc-radius-lg);
    border: 1px solid var(--pc-border);
    background: var(--pc-bg-secondary);
    overflow: hidden;
}

.shadow-sm {
    box-shadow: var(--pc-shadow-sm) !important;
}

/* Product grid responsive */
.product-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    padding: 16px;
}

@media (max-width: 1024px) {
    .product-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 12px;
    }
}

@media (max-width: 768px) {
    .product-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
        padding: 12px;
    }
}

@media (max-width: 480px) {
    .product-grid {
        grid-template-columns: 1fr;
    }
}

/* Mobile styling */
.float-modal-mobile {
    position: relative;
    z-index: 1000;
}

@media (max-width: 767.98px) {
    .main-content-block {
        padding-left: 10px;
        padding-right: 10px;
        margin-top: 15px;
    }
    .mobile-filter-btn { display: flex !important; align-items: center; gap: 6px; margin: 8px 16px; }
}

@media (min-width: 768px) {
    .float-modal-mobile {
        position: sticky;
        top: 20px;
    }
    .breadcrumb-spacing {
        margin-bottom: 20px;
    }
}
</style>